import {Button, Typography} from 'onboarding-components';
import React, {useContext} from 'react';
import {clsx} from 'clsx';

import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets';
import {handleCompleteStep} from 'common/onboarding/immersive-onboarding/utils';
import {TrackingEvent, TrackingProperty, trackOnboardingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {Trans} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';

import common from '../common.module.styl';
import style from './start.module.styl';

interface OnboardingCardProps {
  imgSrc?: string;
  useImgPlaceholder?: boolean;
  title: string;
  description?: string;
  buttonText: string;
  labelText?: string;
  onClick: () => void;
}

const OnboardingCard = ({
  imgSrc,
  useImgPlaceholder,
  title,
  labelText,
  description,
  buttonText,
  onClick,
}: OnboardingCardProps) => (
  <div className={style.card}>
    {imgSrc && (
      <div className={clsx(style.cardImageBox, common.mb16)}>
        {labelText && (
          <div className={clsx(common.label, common.labelMint, style.cardLabel)}>
            <Typography variant='captionXs' weight='bold'>
              {labelText}
            </Typography>
          </div>
        )}
        <img className={style.cardImage} src={imgSrc} alt={title} />
      </div>
    )}
    {useImgPlaceholder && (
      <div className={clsx(style.cardImageBox, common.mb16)}>
        <div className={clsx(style.cardImage, style.cardImagePlaceholder)} />
      </div>
    )}
    <div className={style.cardContent}>
      <div className={common.mb16}>
        <Typography variant='bodyMd' weight='bold'>
          {title}
        </Typography>
        {description && <Typography variant='bodySm'>{description}</Typography>}
      </div>
      <div>
        <Button variant='solid' size='medium' onClick={onClick}>
          {buttonText}
        </Button>
      </div>
    </div>
  </div>
);

export const StartCards = () => {
  const {t} = useTranslation();
  const {setStep, setupGuideInfo, setSetupGuideInfo, setSelectAltSDK} = useContext(SetupGuideContext);
  const {setConnectWithSDK} = useContext(SetupGuideContext);

  const handleCardClick = ({useSDK}: {useSDK: boolean}) => {
    handleCompleteStep({
      setupGuideInfo,
      setSetupGuideInfo,
      setStep,
      completedStep: Step.GetStarted,
      nextStep: Step.Connect,
      trackingEvent: useSDK ? TrackingEvent.StartWithSDKClick : TrackingEvent.StartWithExistingToolClick,
    });

    setSelectAltSDK(false);
    setConnectWithSDK(useSDK);
  };

  return (
    <div className={common.container}>
      <header>
        <Typography className={clsx(common.mt0, common.mb8)} variant='headingSm'>
          {t(`onboarding.welcome.choose-start`)}
        </Typography>
        <Typography className={common.mt0} variant='bodySm'>
          <Trans
            i18nKey={`onboarding.welcome.choose-start-description`}
            t={t}
            components={{
              guideLink: (
                <a
                  href='https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method'
                  target='_blank'
                  rel='noreferrer'
                  onClick={() => {
                    trackOnboardingEvent(TrackingEvent.LinkClicked, {
                      [TrackingProperty.LinkSource]: TrackingEvent.ChooseWhichMethodClick,
                      [TrackingProperty.CurrentStep]: Step.GetStarted,
                    });
                  }}
                />
              ),
            }}
          />
        </Typography>
      </header>
      <div className={clsx(common.grid, common.my32)}>
        <OnboardingCard
          title={t(`onboarding.welcome.quick-start`)}
          description={t(`onboarding.welcome.quick-start-description`)}
          buttonText={t(`onboarding.welcome.quick-start`)}
          labelText={t(`onboarding.welcome.quick-start-label`)}
          onClick={() => handleCardClick({useSDK: true})}
          imgSrc={IMMERSIVE_ASSETS.quickStart}
        />
        <OnboardingCard
          title={t(`onboarding.welcome.already-use-cdp`)}
          description={t(`onboarding.welcome.already-use-cdp-description`)}
          buttonText={t(`onboarding.welcome.connect-data`)}
          onClick={() => handleCardClick({useSDK: false})}
          imgSrc={IMMERSIVE_ASSETS.existingTool}
        />
      </div>
    </div>
  );
};
